/**
 * The kit's one source of viewport-responsive behaviour, and the seam that
 * lets a preview surface lie to it.
 *
 * Every composition decision in `Cresultcard` — which zone a compact action
 * moves to, whether the detail's aside collapses, whether a fact pair is
 * allowed to sit side by side — is hook-driven. There is no `@container`, no
 * `container-type` and no width `@media` anywhere in the kit, because the one
 * absolute frontend rule is that `useViewport()` is the exclusive source of
 * viewport-responsive behaviour and it is enforced literally here.
 *
 * That rule would normally make a preview frame impossible: a `/dev` surface
 * that renders the same card at Wide and at a 390px Mobile mock cannot change
 * the real browser viewport. So the kit reads its viewport through a context
 * seam instead of calling the hook directly. With no provider in scope, the
 * seam resolves to the real `useViewport()` and behaves exactly as the rule
 * describes. With a provider, whatever the host publishes wins — which is how
 * `/dev/pillar-widgets` drives eighteen families from its own preview toggle
 * (`usePillarViewport()`) without the kit importing a single thing from
 * `app/dev`:
 *
 *   const { viewport } = usePillarViewport();
 *   <CresultcardViewportProvider viewport={viewport}>…</CresultcardViewportProvider>
 *
 * The real hook is called on every read even when an override is present. That
 * is deliberate: a conditional hook call is not legal, and the alternative —
 * a provider that is mandatory — turns a forgotten wrapper into a card that is
 * silently wrong on a real phone.
 */
"use client";
import {
  createContext,
  useContext,
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react";
import { Viewport } from "@/theme/breakpoints";
import { useViewport } from "@/hooks/dom/use-viewport";

/**
 * The kit's two-step responsive axis. Three viewports collapse to two here
 * because every composition in the system has exactly two shapes, and a card
 * that behaves differently at Compact than at Web is a card nobody has drawn.
 */
export enum CresultcardWidth {
  Wide = "wide",
  Mobile = "mobile",
}

export interface CresultcardViewportState {
  /** Null only before the media queries resolve after hydration. */
  readonly viewport: Viewport | null;
  readonly width: CresultcardWidth;
  readonly isMobile: boolean;
}

/** `null` means "no override in scope", never "unresolved". */
const CresultcardViewportContext = createContext<Viewport | null>(null);

interface CresultcardViewportProviderProps {
  readonly children: ReactNode;
  /**
   * The viewport every card below this point renders against. Pass `null` to
   * mount the provider without overriding — the seam falls back to the real
   * hook, so a host can wire the provider in before it has a preview mode.
   */
  readonly viewport: Viewport | null;
}

export function CresultcardViewportProvider({
  children,
  viewport,
}: CresultcardViewportProviderProps): ReactElement {
  return (
    <CresultcardViewportContext.Provider value={viewport}>
      {children}
    </CresultcardViewportContext.Provider>
  );
}

/**
 * Resolves to the injected viewport when a provider is in scope and to the
 * real one otherwise. Before hydration resolves the media queries the width is
 * Wide, which is the same first paint the server produced.
 */
export function useCresultcardViewport(): CresultcardViewportState {
  const override = useContext(CresultcardViewportContext);
  const { viewport: real } = useViewport();
  const resolved = override ?? real;

  return useMemo<CresultcardViewportState>(
    (): CresultcardViewportState => ({
      viewport: resolved,
      width:
        resolved === Viewport.Mobile
          ? CresultcardWidth.Mobile
          : CresultcardWidth.Wide,
      isMobile: resolved === Viewport.Mobile,
    }),
    [resolved],
  );
}
